import { useEffect, useState, useCallback, useRef } from 'react';
import { useAppSelector } from '../store/hooks';
import {
  getPortfolioPerformance,
  getPortfolioHistory,
  getPortfolioVolatility,
  getBenchmarks,
} from '../api/portfolio';
import type {
  PerformanceResult,
  PortfolioHistoryResponse,
  VolatilityMetrics,
  BenchmarkData,
} from '../api/portfolio';
import type { TimeRange } from '../components/common/TimeRangeSelector';

interface PortfolioAnalyticsData {
  performance: PerformanceResult | null;
  history: PortfolioHistoryResponse | null;
  volatility: VolatilityMetrics | null;
  benchmarks: BenchmarkData | null;
}

interface PortfolioAnalyticsContext {
  range: TimeRange;
  label: string;
  from: string;
  to: string;
}

interface UsePortfolioAnalyticsResult {
  data: PortfolioAnalyticsData;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  displayedContext: PortfolioAnalyticsContext | null;
  lastUpdatedAt: string | null;
  refetch: () => void;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function getFromDate(range: TimeRange): Date {
  const now = new Date();
  switch (range) {
    case '7d':
      return new Date(now.getTime() - 7 * DAY_MS);
    case '30d':
      return new Date(now.getTime() - 30 * DAY_MS);
    case '90d':
      return new Date(now.getTime() - 90 * DAY_MS);
    case '1y':
      return new Date(now.getTime() - 365 * DAY_MS);
    case 'all':
      // Use Unix epoch as a practical "no lower bound" to request full history.
      return new Date(0);
    default:
      return new Date(now.getTime() - 30 * DAY_MS);
  }
}

function hasAnalyticsData(data: PortfolioAnalyticsData): boolean {
  return data.performance !== null || data.history !== null || data.volatility !== null || data.benchmarks !== null;
}

export function usePortfolioAnalytics(
  range: TimeRange,
  customFrom?: Date,
  customTo?: Date,
  contextLabel?: string,
): UsePortfolioAnalyticsResult {
  const userId = useAppSelector((state) => state.auth.user?.id);

  const [data, setData] = useState<PortfolioAnalyticsData>({
    performance: null,
    history: null,
    volatility: null,
    benchmarks: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayedContext, setDisplayedContext] = useState<PortfolioAnalyticsContext | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const fetchData = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      setIsRefreshing(false);
      return;
    }

    const hadDisplayedData = hasAnalyticsData(dataRef.current);

    if (hadDisplayedData) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const from = customFrom ?? getFromDate(range);
      const to = customTo ?? new Date();
      const requestContext: PortfolioAnalyticsContext = {
        range,
        label: contextLabel ?? range,
        from: from.toISOString(),
        to: to.toISOString(),
      };

      // Map TimeRange to API range param (benchmarks endpoint doesn't support 'all')
      const benchmarkRange = range === 'all' ? '1y' : range;

      const [performanceResult, historyResult, volatilityResult, benchmarksResult] =
        await Promise.allSettled([
          getPortfolioPerformance(userId, from, to),
          getPortfolioHistory(userId, from, to),
          getPortfolioVolatility(userId),
          getBenchmarks(benchmarkRange),
        ]);

      const nextData: PortfolioAnalyticsData = {
        performance:
          performanceResult.status === 'fulfilled' ? performanceResult.value : null,
        history:
          historyResult.status === 'fulfilled' ? historyResult.value : null,
        volatility:
          volatilityResult.status === 'fulfilled' ? volatilityResult.value : null,
        benchmarks:
          benchmarksResult.status === 'fulfilled' ? benchmarksResult.value : null,
      };

      const errors: string[] = [];
      if (performanceResult.status === 'rejected') {
        errors.push('Performance data unavailable');
      }
      if (historyResult.status === 'rejected') {
        errors.push('History data unavailable');
      }
      if (volatilityResult.status === 'rejected') {
        errors.push('Volatility data unavailable');
      }

      if (errors.length === 3) {
        setError('Failed to load analytics data. Please try again.');
      } else if (errors.length > 0) {
        setError(errors.join('. '));
      }

      if (!hadDisplayedData || errors.length === 0) {
        setData(nextData);
        setDisplayedContext(requestContext);
        setLastUpdatedAt(new Date().toISOString());
      } else if (!hasAnalyticsData(dataRef.current) && hasAnalyticsData(nextData)) {
        setData(nextData);
        setDisplayedContext(requestContext);
        setLastUpdatedAt(new Date().toISOString());
      }
    } catch {
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [userId, range, customFrom, customTo, contextLabel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, isRefreshing, error, displayedContext, lastUpdatedAt, refetch: fetchData };
}
